import React, { useState, useMemo, useEffect } from "react";
import { Box, Select, MenuItem } from "@mui/material";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import SyncIcon from "@mui/icons-material/Sync";
import ContentPasteIcon from "@mui/icons-material/ContentPaste";
import RadioButtonCheckedIcon from "@mui/icons-material/RadioButtonChecked";
import StarIcon from "@mui/icons-material/Star";
import NotesIcon from "@mui/icons-material/Notes";
import { useTasksTab } from "../../hooks/Tasks/useTasksTab";
import { CARD_SX, PRIMARY_COLOR } from "./constants";
import { KeyUpdateWithTask, KeyUpdateType, TargetStatus } from "./types";
import StatusBadge from "./StatusBadge";
import { TimelineSkeleton } from "./Skeletons";
import AddNoteModal from "./AddNoteModal";
import { formatRelativeTime } from "../shared/helpers/formatRelativeTime";
import { tokens } from "../../theme/tokens";

// ─── Icon map ─────────────────────────────────────────────────────────────────
const TIMELINE_ICON_MAP: Record<KeyUpdateType, { Icon: React.ElementType; color: string }> = {
  task_created: { Icon: AddCircleIcon, color: tokens.color.brand.primary },
  task_status_changed: { Icon: SyncIcon, color: tokens.color.text.secondary },
  targets_added: { Icon: ContentPasteIcon, color: tokens.color.brand.primary },
  target_status_changed: { Icon: RadioButtonCheckedIcon, color: tokens.color.text.secondary },
  target_made_primary: { Icon: StarIcon, color: tokens.color.workflow.doing.fg },
  note: { Icon: NotesIcon, color: tokens.color.text.secondary },
};

const TARGET_STATUS_COLORS: Partial<Record<TargetStatus, string>> = {
  WORKED: tokens.color.workflow.worked.fg,
  BAD: tokens.color.status.error,
  DOING: tokens.color.workflow.doing.fg,
};

function getTimelineIconColor(ku: KeyUpdateWithTask): string {
  if (ku.type === "target_status_changed" && ku.new_status) {
    return TARGET_STATUS_COLORS[ku.new_status] ?? TIMELINE_ICON_MAP[ku.type].color;
  }
  return TIMELINE_ICON_MAP[ku.type].color;
}

function formatAbsoluteTime(iso: string): string {
  return new Date(iso).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

function truncateUserId(uid: string): string {
  return uid.length > 10 ? `…${uid.slice(-10)}` : uid;
}

// ─── Filter config ────────────────────────────────────────────────────────────
type TimelineEventFilter =
  | "all"
  | "notes"
  | "status_changes"
  | "created"
  | "starred"
  | "objectives_added";

const EVENT_FILTER_TYPES: Record<TimelineEventFilter, KeyUpdateType[]> = {
  all: [],
  notes: ["note"],
  status_changes: ["task_status_changed", "target_status_changed"],
  created: ["task_created"],
  starred: ["target_made_primary"],
  objectives_added: ["targets_added"],
};

const EVENT_FILTER_LABELS: Record<TimelineEventFilter, string> = {
  all: "All Types",
  notes: "Notes",
  status_changes: "Status Changes",
  created: "Created",
  starred: "Starred",
  objectives_added: "Objectives Added",
};

const ActivityTimeline: React.FC = () => {
  const { tasks, selectedTask, addNote, isLoading, userMap } = useTasksTab();

  const [noteDialog, setNoteDialog] = useState<{
    taskId: string;
    taskTitle: string;
  } | null>(null);
  const [filterEvent, setFilterEvent] = useState<TimelineEventFilter>("all");
  const [filterTaskId, setFilterTaskId] = useState("");
  const [filterUserId, setFilterUserId] = useState("");

  // Timeline feeds from all tasks across all POIs — not filtered by activePoiId (intentional)
  const allUpdates = useMemo<KeyUpdateWithTask[]>(
    () =>
      tasks
        .flatMap((t) =>
          t.keyUpdates.map(
            (ku): KeyUpdateWithTask => ({
              ...ku,
              taskTitle: t.title,
              poiName: t.poi.name,
            }),
          ),
        )
        .sort((a, b) => b.created_at.localeCompare(a.created_at)),
    [tasks],
  );

  const uniqueTaskOptions = useMemo(() => {
    const seen = new Set<string>();
    return allUpdates.reduce<{ task_id: string; taskTitle: string }[]>((acc, ku) => {
      if (!seen.has(ku.task_id)) {
        seen.add(ku.task_id);
        acc.push({ task_id: ku.task_id, taskTitle: ku.taskTitle });
      }
      return acc;
    }, []);
  }, [allUpdates]);

  const uniqueUserOptions = useMemo(() => {
    const seen = new Set<string>();
    return allUpdates.reduce<string[]>((acc, ku) => {
      if (ku.created_by_user_id && !seen.has(ku.created_by_user_id)) {
        seen.add(ku.created_by_user_id);
        acc.push(ku.created_by_user_id);
      }
      return acc;
    }, []);
  }, [allUpdates]);

  const filteredUpdates = useMemo(() => {
    const typeFilters = EVENT_FILTER_TYPES[filterEvent];
    return allUpdates
      .filter((ku) => {
        if (typeFilters.length > 0 && !typeFilters.includes(ku.type)) return false;
        if (filterTaskId && ku.task_id !== filterTaskId) return false;
        if (filterUserId && ku.created_by_user_id !== filterUserId) return false;
        return true;
      })
      .slice(0, 20);
  }, [allUpdates, filterEvent, filterTaskId, filterUserId]);

  useEffect(() => {
    if (filterTaskId && !uniqueTaskOptions.some((o) => o.task_id === filterTaskId)) {
      setFilterTaskId("");
    }
  }, [uniqueTaskOptions, filterTaskId]);

  useEffect(() => {
    if (filterUserId && !uniqueUserOptions.includes(filterUserId)) {
      setFilterUserId("");
    }
  }, [uniqueUserOptions, filterUserId]);

  const handleAddNote = (note: string) => {
    if (!noteDialog) return;
    addNote(noteDialog.taskId, note);
    setNoteDialog(null);
  };

  const anyFilterActive = filterEvent !== "all" || !!filterTaskId || !!filterUserId;

  return (
    <Box
      sx={{
        ...CARD_SX,
        flexShrink: 0,
        maxHeight: "260px",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: "18px",
          py: "10px",
          borderBottom: `1px solid ${tokens.color.border.default}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <span
            style={{
              fontSize: "10px",
              fontWeight: 700,
              color: tokens.color.text.secondary,
              textTransform: "uppercase",
              letterSpacing: "0.08em",
            }}
          >
            Activity Timeline
          </span>
          {anyFilterActive && (
            <Box
              sx={{
                width: "6px",
                height: "6px",
                borderRadius: "50%",
                backgroundColor: tokens.color.brand.primary,
                flexShrink: 0,
              }}
            />
          )}
        </Box>

        <button
          type="button"
          onClick={() =>
            selectedTask &&
            setNoteDialog({ taskId: selectedTask.task_id, taskTitle: selectedTask.title })
          }
          disabled={!selectedTask}
          title={
            selectedTask
              ? `Add note to "${selectedTask.title}"`
              : "Select a task to add a note"
          }
          style={{
            display: "flex",
            alignItems: "center",
            gap: "4px",
            fontSize: "11px",
            fontWeight: 500,
            color: selectedTask ? PRIMARY_COLOR : tokens.color.text.secondary,
            backgroundColor: "transparent",
            border: `1px solid ${tokens.color.border.default}`,
            borderRadius: "6px",
            padding: "3px 10px",
            cursor: selectedTask ? "pointer" : "not-allowed",
            height: "26px",
            opacity: selectedTask ? 1 : 0.6,
            transition: "background-color 0.15s ease",
          }}
        >
          <NotesIcon style={{ fontSize: "13px" }} />
          Add Note
        </button>
      </Box>

      {/* Filter row — hidden during load and when there is no activity */}
      {!isLoading && allUpdates.length > 0 && (
        <Box
          sx={{
            px: "18px",
            py: "6px",
            borderBottom: `1px solid ${tokens.color.border.default}`,
            display: "flex",
            alignItems: "center",
            gap: "6px",
            flexShrink: 0,
          }}
        >
          <Select
            size="small"
            value={filterEvent}
            onChange={(e) => setFilterEvent(e.target.value as TimelineEventFilter)}
            sx={{
              fontSize: "11px",
              height: "26px",
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: filterEvent !== "all" ? `${tokens.color.brand.primary}` : `${tokens.color.border.default}`,
              },
              "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: tokens.color.border.strong },
              "& .MuiSelect-select": { py: "3px", pr: "24px !important", fontSize: "11px" },
            }}
          >
            {(Object.keys(EVENT_FILTER_LABELS) as TimelineEventFilter[]).map((key) => (
              <MenuItem key={key} value={key} sx={{ fontSize: "11px" }}>
                {EVENT_FILTER_LABELS[key]}
              </MenuItem>
            ))}
          </Select>

          <Select
            size="small"
            value={filterTaskId}
            onChange={(e) => setFilterTaskId(e.target.value)}
            displayEmpty
            disabled={uniqueTaskOptions.length === 0}
            sx={{
              fontSize: "11px",
              height: "26px",
              minWidth: "120px",
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: filterTaskId ? `${tokens.color.brand.primary}` : `${tokens.color.border.default}`,
              },
              "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: tokens.color.border.strong },
              "& .MuiSelect-select": { py: "3px", fontSize: "11px" },
            }}
          >
            <MenuItem value="" sx={{ fontSize: "11px" }}>All Tasks</MenuItem>
            {uniqueTaskOptions.map((opt) => (
              <MenuItem key={opt.task_id} value={opt.task_id} sx={{ fontSize: "11px" }}>
                {opt.taskTitle}
              </MenuItem>
            ))}
          </Select>

          <Select
            size="small"
            value={filterUserId}
            onChange={(e) => setFilterUserId(e.target.value)}
            displayEmpty
            disabled={uniqueUserOptions.length === 0}
            sx={{
              fontSize: "11px",
              height: "26px",
              minWidth: "100px",
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: filterUserId ? `${tokens.color.brand.primary}` : `${tokens.color.border.default}`,
              },
              "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: tokens.color.border.strong },
              "& .MuiSelect-select": { py: "3px", fontSize: "11px" },
            }}
          >
            <MenuItem value="" sx={{ fontSize: "11px" }}>All Users</MenuItem>
            {uniqueUserOptions.map((uid) => (
              <MenuItem key={uid} value={uid} sx={{ fontSize: "11px" }}>
                {userMap[uid] ?? truncateUserId(uid)}
              </MenuItem>
            ))}
          </Select>
        </Box>
      )}

      {/* Entries */}
      <Box sx={{ flex: 1, overflowY: "auto", px: "18px", py: "8px" }}>
        {isLoading ? (
          <TimelineSkeleton />
        ) : allUpdates.length === 0 ? (
          <span style={{ fontSize: "12px", color: tokens.color.border.strong }}>No activity yet.</span>
        ) : filteredUpdates.length === 0 ? (
          <span style={{ fontSize: "12px", color: tokens.color.border.strong }}>
            No results for these filters.
          </span>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            {filteredUpdates.map((ku) => {
              const statusChange =
                ku.type === "target_status_changed" && ku.new_status
                  ? { prev: ku.prev_status, next: ku.new_status }
                  : null;
              const { Icon } = TIMELINE_ICON_MAP[ku.type];

              return (
                <Box
                  key={ku.update_id}
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                    gap: "10px",
                    py: "6px",
                    borderBottom: `1px solid ${tokens.color.border.default}`,
                    "&:last-child": { borderBottom: "none" },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: "8px",
                      flex: 1,
                      minWidth: 0,
                    }}
                  >
                    <Icon
                      sx={{
                        fontSize: "16px",
                        color: getTimelineIconColor(ku),
                        mt: "1px",
                        flexShrink: 0,
                      }}
                    />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      {statusChange ? (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                            flexWrap: "wrap",
                            mb: "1px",
                          }}
                        >
                          <span
                            style={{
                              fontSize: "12px",
                              color: tokens.color.text.primary,
                              fontWeight: 500,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              maxWidth: "120px",
                            }}
                          >
                            {ku.target_value ?? ku.note}
                          </span>
                          {statusChange.prev ? (
                            <StatusBadge status={statusChange.prev} />
                          ) : (
                            <span style={{ fontSize: "10px", color: tokens.color.border.strong }}>—</span>
                          )}
                          <span style={{ fontSize: "10px", color: tokens.color.text.secondary }}>→</span>
                          <StatusBadge status={statusChange.next} />
                        </Box>
                      ) : (
                        <span
                          style={{ fontSize: "12px", color: tokens.color.text.primary, display: "block" }}
                        >
                          {ku.note}
                        </span>
                      )}
                      <span style={{ fontSize: "10px", color: tokens.color.border.strong }}>
                        {ku.taskTitle} · {ku.poiName}
                      </span>
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-end",
                      gap: "2px",
                      flexShrink: 0,
                    }}
                  >
                    <span style={{ fontSize: "10px", color: tokens.color.text.secondary, whiteSpace: "nowrap" }}>
                      {formatRelativeTime(ku.created_at)}
                    </span>
                    <span style={{ fontSize: "9px", color: tokens.color.border.strong, whiteSpace: "nowrap" }}>
                      {ku.created_by_user_id
                        ? `by ${userMap[ku.created_by_user_id] ?? truncateUserId(ku.created_by_user_id)} · ${formatAbsoluteTime(ku.created_at)}`
                        : formatAbsoluteTime(ku.created_at)}
                    </span>
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </Box>

      <AddNoteModal
        open={noteDialog !== null}
        onClose={() => setNoteDialog(null)}
        taskTitle={noteDialog?.taskTitle ?? ""}
        onSubmit={handleAddNote}
      />
    </Box>
  );
};

export default ActivityTimeline;
